import { Injectable } from "@angular/core"
import { Observable, map } from "rxjs"
import { environment } from "../../environments/environment"
import { ApiService } from "./api.service"
import { collapseRepeatedSpaces } from "../utils/text"

export const SIGNUP_PURPOSE_OTHER = "other"
export const SIGNUP_PURPOSE_OTHER_MAX = 200

export interface SignupPurposeCategory {
  id: number
  label: string
  is_active?: boolean
  sort_order?: number
}

@Injectable({ providedIn: "root" })
export class SignupPurposeCategoriesService {
  private readonly publicUrl = `${environment.apiUrl}/signup/purpose-categories`
  private readonly adminUrl = `${environment.apiUrl}/admin/signup-purpose-categories`

  constructor(private api: ApiService) {}

  getPublicCategories(): Observable<SignupPurposeCategory[]> {
    return this.api.publicGet<unknown>(this.publicUrl).pipe(
      map((res) => this.parseCategories(res, { activeOnly: true })),
    )
  }

  getAdminCategories(): Observable<SignupPurposeCategory[]> {
    return this.api.authenticatedGet<unknown>(this.adminUrl).pipe(
      map((res) => this.parseCategories(res, { activeOnly: false })),
    )
  }

  createCategory(label: string): Observable<unknown> {
    return this.api.authenticatedPost(this.adminUrl, {
      label: collapseRepeatedSpaces(label),
    })
  }

  updateCategory(
    id: number,
    patch: { label?: string; is_active?: boolean },
  ): Observable<unknown> {
    const body: { label?: string; is_active?: boolean } = {}
    if (patch.label !== undefined) {
      body.label = collapseRepeatedSpaces(patch.label)
    }
    if (patch.is_active !== undefined) {
      body.is_active = patch.is_active
    }
    return this.api.authenticatedPut(`${this.adminUrl}/${id}`, body)
  }

  deleteCategory(id: number): Observable<unknown> {
    return this.api.authenticatedDelete(`${this.adminUrl}/${id}`)
  }

  private parseCategories(
    res: unknown,
    opts: { activeOnly: boolean },
  ): SignupPurposeCategory[] {
    const root = (res && typeof res === "object" ? res : {}) as Record<string, unknown>
    const nested = (root["data"] && typeof root["data"] === "object"
      ? (root["data"] as Record<string, unknown>)
      : null)
    const raw =
      root["categories"] ??
      nested?.["categories"] ??
      root["records"] ??
      (Array.isArray(root["data"]) ? root["data"] : null) ??
      (Array.isArray(res) ? res : [])

    const rows = Array.isArray(raw) ? raw : []
    const categories = rows
      .map((row) => this.normalizeCategory(row))
      .filter((row): row is SignupPurposeCategory => row != null)

    if (opts.activeOnly) {
      return categories.filter((row) => row.is_active !== false)
    }
    return categories
  }

  private normalizeCategory(row: unknown): SignupPurposeCategory | null {
    if (!row || typeof row !== "object") return null
    const rec = row as Record<string, unknown>
    const id = Number(rec["id"])
    const label = String(rec["label"] ?? rec["name"] ?? "").trim()
    if (!Number.isFinite(id) || id <= 0 || !label) return null
    const activeRaw = rec["is_active"]
    const is_active =
      activeRaw === undefined || activeRaw === null
        ? true
        : activeRaw === true || activeRaw === 1 || activeRaw === "1" || activeRaw === "true"
    const sort_order = rec["sort_order"] == null ? undefined : Number(rec["sort_order"])
    return {
      id,
      label,
      is_active,
      sort_order: Number.isFinite(sort_order) ? sort_order : undefined,
    }
  }
}
